import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Model from '../components/Model';
import WorkFooter from '../components/WorkFooter';
import SideMenu from '../components/SideMenu';
import ProjectOverlay from '../components/ProjectOverlay';
import { useBrowserInfo } from '../hooks/useBrowserInfo';
import ProjectMenu from '../components/ProjectMenu';
import ProjectCarousel from '../components/ProjectCarousel';
import transition from '../transition';

function Work(props) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [threeJSStats, setThreeJSStats] = useState({ fps: 0 });

  const [selectedTexture, setSelectedTexture] = useState('default');
  const [hoveredId, setHoveredId] = useState(null);
  const [hidingMenu, setHidingMenu] = useState(false);
  const hideTimeoutRef = React.useRef(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const overlayHistoryPushedRef = React.useRef(false);
  const [textureLocked, setTextureLocked] = useState(false);
  const overlayHistoryRef = React.useRef(false);

  const [menuReturning, setMenuReturning] = useState(false);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [enterReady, setEnterReady] = useState(false);
  const [introDone, setIntroDone] = useState(false);

  const rippleDisabled = true;
  const { windowSize } = useBrowserInfo();
  const isMobile = windowSize.width <= 768;
  const [wireframe, setWireframe] = useState(false);
  const [lightsOn, setLightsOn] = useState(true);
  const [gridOn, setGridOn] = useState(false);

  const handleOrientationUpdate = () => {};

  const handleMenuItemClick = (itemId) => {
    if (isAnimating || isZoomed) return;
    setSelectedTexture(itemId || 'default');
    setHoveredId(itemId || null);
    setTextureLocked(true);
    setIsAnimating(true);
    setHidingMenu(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsZoomed(true);
      setHidingMenu(false);
    }, 0);
  };

  const handleZoomComplete = (info) => {
    setIsAnimating(false);
    if (info && info.zoomingOut) {
      setTextureLocked(false);
      setMenuReturning(true);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => setMenuReturning(false), 700);
      return;
    }
    setOverlayOpen(true);
  };

  const handleHoverChange = (textureId) => {
    if (textureLocked || isAnimating || isZoomed || hidingMenu) return;
    setSelectedTexture(textureId || 'default');
    setHoveredId(textureId && textureId !== 'default' ? textureId : null);
  };

  const handleStatsUpdate = (stats) => setThreeJSStats(stats);

  React.useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('workZoomChange', { detail: { zoomed: isZoomed } })); } catch(_) {}
  }, [isZoomed]);

  // If navigation initiates to a different route, clear hover to avoid abrupt carousel changes
  React.useEffect(() => {
    const onNav = (e) => {
      try {
        const to = e.detail && e.detail.to;
        if (to && to !== '/') {
          setHoveredId(null);
        }
      } catch(_) {}
    };
    window.addEventListener('navNavigating', onNav);
    return () => window.removeEventListener('navNavigating', onNav);
  }, []);

  // When overlay opens, push a history state so Back closes it
  React.useEffect(() => {
    if (overlayOpen) {
      try {
        window.history.pushState({ overlay: true }, '', window.location.href);
        overlayHistoryPushedRef.current = true;
      } catch(_) {}
    }
  }, [overlayOpen]);

  // Listen for back button to close overlay if open
  React.useEffect(() => {
    const onPop = (e) => {
      if (overlayHistoryPushedRef.current && overlayOpen) {
        try { setOverlayOpen(false); } catch(_) {}
        overlayHistoryPushedRef.current = false;
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [overlayOpen]);

  return (
    <main className={`app-container ${isZoomed ? 'is-zoomed' : ''}`}>
      <section id="work-section">
        <Model
          isZoomed={isZoomed}
          onZoomComplete={handleZoomComplete}
          onStatsUpdate={handleStatsUpdate}
          selectedTexture={selectedTexture}
          introActive={false}
          infoActiveId={hoveredId ? hoveredId : (isZoomed ? selectedTexture : null)}
          loadingFinished={loadingFinished}
          rippleDisabled={rippleDisabled}
          onIntroComplete={() => setIntroDone(true)}
          onReadyForDisplay={() => {
            if (!enterReady) {
              setEnterReady(true);
              setLoadingFinished(true);
              try { window.dispatchEvent(new Event('pageEnterReady')); } catch (_) {}
            }
          }}
          onOrientationUpdate={handleOrientationUpdate}
          wireframe={wireframe}
          lightsOn={lightsOn}
          gridVisible={gridOn}
          onZoomFxProgress={(v) => {
            if (isZoomed && !overlayOpen && v >= 0.75) {
              setOverlayOpen(true);
            }
          }}
        />

        {!isMobile && (
          <>
            <SideMenu side="left" selectedTexture={selectedTexture} onHoverChange={handleHoverChange} />
            <SideMenu side="right" selectedTexture={selectedTexture} onHoverChange={handleHoverChange} />
          </>
        )}

        {isMobile ? (
          <motion.div
            className="mobile-carousel-anchor"
            style={{ width: '100%' }}
            initial={false}
            animate={{ y: isZoomed ? 190 : 0 }}
            transition={{ type: 'tween', ease: [0.22, 1, 0.36, 1], duration: 0.38 }}
          >
            <ProjectCarousel
              currentId={selectedTexture || hoveredId || 'default'}
              onSelect={(id) => { handleHoverChange(id); }}
              onViewDetails={(id) => handleMenuItemClick(id)}
            />
          </motion.div>
        ) : (
          <div className="work-menu">
            <ProjectMenu
              className="is-embedded"
              onMenuItemClick={handleMenuItemClick}
              onHoverChange={handleHoverChange}
              hoveredId={hoveredId}
              hideOnZoom={isZoomed}
              hide={hidingMenu || (!enterReady && !isMobile)}
              disableHover={textureLocked || isZoomed || hidingMenu}
              returning={menuReturning}
            />
          </div>
        )}
      </section>

      <WorkFooter
        threeJSStats={threeJSStats}
        isZoomed={isZoomed}
        hide={hidingMenu || (!introDone && !isMobile)}
        returning={menuReturning}
        wireframeEnabled={wireframe}
        onToggleWireframe={setWireframe}
        gridEnabled={gridOn}
        onToggleGrid={setGridOn}
      />

      <ProjectOverlay
        open={overlayOpen}
        projectId={selectedTexture}
        enterDelay={0.0}
        onClose={() => {
          setOverlayOpen(false);
          setHoveredId(null);
          // If we pushed a history entry for the overlay, go back once to keep stack clean
          if (overlayHistoryPushedRef.current) {
            overlayHistoryPushedRef.current = false;
            try { window.history.back(); } catch(_) {}
          }
        }}
        onExitComplete={() => {
          if (!isAnimating && isZoomed) {
            setIsAnimating(true);
            setIsZoomed(false);
          }
        }}
      />
    </main>
  );
}

export default transition(Work);